import { useCallback, useState } from "react";
import { Tab, Tabs } from "react-bootstrap";
import { AppCard } from "@/layout/AppComponents";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import ReactVisibilitySensor from "react-visibility-sensor";
import ReactDOMServer from "react-dom/server";
import InformationTab from "./InformationTab";
import LegalTab from "./LegalTab";
import EvidenceTab from "./EvidenceTab";
import CalendarTab from "./CalendarTab";
import CorrectiveActionsTab from "./CorrectiveActionsTab";
import ResponsibleTab from "./ResponsibleTab";
import SanctionTab from "./SanctionTab";
import Translator from "../../../../../components/helpers/Translator";
import CustomTextsTab from "./CustomTextsTab";
import NoAcceptanceTab from "./NoAcceptanceTab";
import AccionaPenaltyRiskTab from "./PenaltyRisk/AccionaPenaltyRiskTab";
import CopisaPenaltyRiskTab from "./PenaltyRisk/CopisaPenaltyRiskTab";
import PlantObligationClientElementsTab from "./PlantObligationClientElementsTab";
import HaizeaPenaltyRiskTab from "./PenaltyRisk/HaizeaPenaltyRiskTab";
import ResourceManagerTab from "./ResourceManagerTab";
import _ from "lodash";

const ExpandedContainer = (props) => {
  const { plantObligationID, active } = props;
  const { moduleBridgeHex } = useParams();
  const locale = useSelector((state) => state.global.urlData.lang);
  const auth = useSelector((state) => state.auth);
  const permissions = useSelector(
    (state) => state.module[moduleBridgeHex].permissions,
  );
  const userBridgeSettings = useSelector(
    (s) => s.module?.[moduleBridgeHex]?.userBridge?.permissions,
  );
  const currentPage = useSelector((state) => state.currentPage);

  const [activeTab, setActiveTab] = useState(active || "info");

  const renderPenaltyRisk = useCallback(() => {
    switch (permissions?.gcl?.module?.penaltyRisk?.type) {
      case "acciona":
        return (
          <Tab
            eventKey="accionaPenaltyRisk"
            title="Riesgos Penales"
            tabClassName={`pt-3 ${activeTab === "accionaPenaltyRisk" ? "border border-bottom-0" : "border-0"} bg-white`}
          >
            {activeTab === "accionaPenaltyRisk" && (
              <AccionaPenaltyRiskTab plantObligationID={plantObligationID} />
            )}
          </Tab>
        );
      case "copisa":
        return (
          <Tab
            eventKey="copisaPenaltyRisk"
            title="Riesgos Penales"
            tabClassName={`pt-3 ${activeTab === "copisaPenaltyRisk" ? "border border-bottom-0" : "border-0"} bg-white`}
          >
            {activeTab === "copisaPenaltyRisk" && (
              <CopisaPenaltyRiskTab plantObligationID={plantObligationID} />
            )}
          </Tab>
        );
      case "haizea":
        return (
          <Tab
            eventKey="haizea"
            title="Riesgos Penales"
            tabClassName={`pt-3 ${activeTab === "haizea" ? "border border-bottom-0" : "border-0"} bg-white`}
          >
            {activeTab === "haizea" && (
              <HaizeaPenaltyRiskTab plantObligationID={plantObligationID} />
            )}
          </Tab>
        );
      default:
        return null;
    }
  }, [permissions, activeTab, plantObligationID]);

  return (
    <AppCard bodyClassName="p-0 mt-2">
      <Tabs
        navStyle={{ paddingBottom: "0.10rem" }}
        defaultActiveKey={activeTab}
        onSelect={(v) => setActiveTab(v)}
      >
        <Tab
          eventKey="info"
          title={ReactDOMServer.renderToString(
            <Translator file="user" name="user.info" />,
          )}
          tabClassName={`pt-3 ${activeTab === "info" ? "border border-bottom-0" : "border-0"} bg-white`}
        >
          {activeTab === "info" && (
            <InformationTab plantObligationID={plantObligationID} />
          )}
        </Tab>
        {!permissions?.gcl?.module?.plant?.hideLawTab && (
          <Tab
            eventKey="legal"
            title={ReactDOMServer.renderToString(
              <Translator
                file="module-legalCompliance"
                name="module.lc.legal-reference"
              />,
            )}
            tabClassName={`pt-3 ${activeTab === "legal" ? "border border-bottom-0" : "border-0"} bg-white`}
          >
            {activeTab === "legal" && (
              <LegalTab plantObligationID={plantObligationID} />
            )}
          </Tab>
        )}
        {permissions?.gcl?.module?.plant?.evidences &&
          (userBridgeSettings?.level !== 2 ||
            userBridgeSettings?.evidences?.read === true) && (
            <Tab
              eventKey="evidences"
              title={ReactDOMServer.renderToString(
                <Translator file="module-legalCompliance" name="evidences" />,
              )}
              tabClassName={`pt-3 ${activeTab === "evidences" ? "border border-bottom-0" : "border-0"} bg-white`}
            >
              {activeTab === "evidences" && (
                <EvidenceTab plantObligationID={plantObligationID} />
              )}
            </Tab>
          )}
        {permissions?.gcl?.module?.plant?.calendar &&
          (userBridgeSettings?.level !== 2 ||
            userBridgeSettings?.writeCalendar === true) && (
            <Tab
              eventKey="calendar"
              title={ReactDOMServer.renderToString(
                <Translator file="user" name="user.calendar" />,
              )}
              tabClassName={`pt-3 ${activeTab === "calendar" ? "border border-bottom-0" : "border-0"} bg-white`}
            >
              {activeTab === "calendar" && (
                <CalendarTab plantObligationID={plantObligationID} />
              )}
            </Tab>
          )}
        {permissions?.gcl?.module?.submodules?.correctiveActions &&
          currentPage.options.hasCaSubmodule && (
            <Tab
              eventKey="correctiveActions"
              title={ReactDOMServer.renderToString(
                <Translator file="module-legalCompliance" name="action.plan" />,
              )}
              tabClassName={`pt-3 ${activeTab === "correctiveActions" ? "border border-bottom-0" : "border-0"} bg-white`}
            >
              {activeTab === "correctiveActions" && (
                <CorrectiveActionsTab plantObligationID={plantObligationID} />
              )}
            </Tab>
          )}
        {permissions?.gcl?.module?.plant?.sanction && (
          <Tab
            eventKey="sanction"
            title={ReactDOMServer.renderToString(
              <Translator
                file="module-legalCompliance"
                name="module.lc.additional.info"
              />,
            )}
            tabClassName={`pt-3 ${activeTab === "sanction" ? "border border-bottom-0" : "border-0"} bg-white`}
          >
            {activeTab === "sanction" && (
              <SanctionTab plantObligationID={plantObligationID} />
            )}
          </Tab>
        )}
        {permissions?.gcl?.module?.api?.send && (
          <Tab
            eventKey="noAcceptance"
            title={ReactDOMServer.renderToString(
              <Translator
                file="module-legalCompliance"
                name="module.lc.no.consent"
              />,
            )}
            tabClassName={`pt-3 ${activeTab === "noAcceptance" ? "border border-bottom-0" : "border-0"} bg-white`}
          >
            {activeTab === "noAcceptance" && (
              <NoAcceptanceTab plantObligationID={plantObligationID} />
            )}
          </Tab>
        )}
        {(!permissions?.gcl?.module?.plant?.customObligationTexts?.onlyUsers ||
          permissions?.gcl?.module?.plant?.customObligationTexts?.onlyUsers?.users?.includes(
            auth?.user?.id,
          )) &&
          (permissions?.gcl?.module?.plant?.customObligationTexts?.evidence ||
            permissions?.gcl?.module?.plant?.customObligationTexts
              ?.nextDate) && (
            <Tab
              eventKey="customObligationTexts"
              title={ReactDOMServer.renderToString(
                <Translator
                  file="module-legalCompliance"
                  name="custom.texts"
                />,
              )}
              tabClassName={`pt-3 ${activeTab === "customObligationTexts" ? "border border-bottom-0" : "border-0"} bg-white`}
            >
              {activeTab === "customObligationTexts" && (
                <CustomTextsTab plantObligationID={plantObligationID} />
              )}
            </Tab>
          )}
        {permissions?.gcl?.module?.plant?.gclResponsibles && (
          <Tab
            eventKey="responsibles"
            title={ReactDOMServer.renderToString(
              <Translator file="module-legalCompliance" name="responsibles" />,
            )}
            tabClassName={`pt-3 ${activeTab === "responsibles" ? "border border-bottom-0" : "border-0"} bg-white`}
          >
            {activeTab === "responsibles" && (
              <ResponsibleTab plantObligationID={plantObligationID} />
            )}
          </Tab>
        )}
        {permissions?.gcl?.module?.plant?.resourceManager && (
          <Tab
            eventKey="resourceManager"
            title="Activos"
            tabClassName={`pt-3 ${activeTab === "resourceManager" ? "border border-bottom-0" : "border-0"} bg-white`}
          >
            {activeTab === "resourceManager" && (
              <ResourceManagerTab plantObligationID={plantObligationID} />
            )}
          </Tab>
        )}
        {permissions?.gcl?.module?.plant?.plantObligationClientElements &&
          !_.isEmpty(
            permissions?.gcl?.module?.plant?.plantObligationClientElements,
          ) && (
            <Tab
              eventKey="plantObligationClientElements"
              title={
                permissions?.gcl?.module?.plant?.plantObligationClientElements[
                  locale
                ] ||
                permissions?.gcl?.module?.plant?.plantObligationClientElements[
                  Object.keys(
                    permissions?.gcl?.module?.plant
                      ?.plantObligationClientElements,
                  )[0]
                ]
              }
              // title={ReactDOMServer.renderToString(
              //   <Translator file="module-legalCompliance" name="responsibles" />
              // )}
              tabClassName={`pt-3 ${activeTab === "plantObligationClientElements" ? "border border-bottom-0" : "border-0"} bg-white`}
            >
              {activeTab === "plantObligationClientElements" && (
                <PlantObligationClientElementsTab
                  plantObligationID={plantObligationID}
                />
              )}
            </Tab>
          )}
        {renderPenaltyRisk()}
      </Tabs>
    </AppCard>
  );
};
export default ExpandedContainer;
